import { gql } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from "react";

import { Permission } from "~/hooks/permissions";
import PermissionOrHide from "~/components/common/PermissionOrHide";

import { CalendarBlockBlockTypeEnum, CalendarBlockEventStatusEnum, UpdateCalendarBlockMutationVariables, useUpdateCalendarBlockMutation, useUpdateCalendarBlockStatusMutation } from "~/graphql/__generated__/graphql";
import { LocalCalendarBlock } from "~/hooks/visitDragDropReducer";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { timezoneAbbreviation } from "@caredna-marlin/utils/dates";
import CalendarBlockEditForm, { isCalendarBlockValid } from "./CalendarBlockEditForm";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { capitalize, startCase, unset } from "lodash/fp";
import { defaultCalendarBlockTitles } from "@caredna-marlin/utils/calendarBlockHelper";
import { Link } from "react-router-dom";
import { startOfDay } from "date-fns";

type Props = {
  calendarBlock: LocalCalendarBlock;
  onClose: () => void;
};

const EditForm = CalendarBlockEditForm<UpdateCalendarBlockMutationVariables & { providerId: number, blockType: CalendarBlockBlockTypeEnum }>;

export default function CalendarBlockPopup({
  calendarBlock,
  onClose,
}: Props) {
  const { visitTypes: visitTypeByKey } = useOrgConfig();
  const [updateCalendarBlock, { loading }] = useUpdateCalendarBlockMutation();
  const [updateStatus, { loading: statusLoading }] = useUpdateCalendarBlockStatusMutation();
  const [local, setLocal] = useState<UpdateCalendarBlockMutationVariables & { providerId: number, blockType: CalendarBlockBlockTypeEnum } | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { timeRangeLabel } = useZonedDateFormat();

  const onEditSave = async () => {
    if (!local) {
      setLocal({ ...calendarBlock, addressPlaceId: '' });
      return;
    }

    await updateCalendarBlock({
      variables: unset("filters.__typename", local),
      awaitRefetchQueries: true,
      refetchQueries: ["CommandIndexCombinedData", "VisitAlerts", "VisitLocations", "AvailabilityByTimespan"],
    });
    onClose();
  };

  const onCancel = async () => {
    await updateStatus({
      variables: { id: calendarBlock.id, eventStatus: CalendarBlockEventStatusEnum.Canceled },
      refetchQueries: ["CommandIndexCombinedData", "VisitAlerts", "VisitLocations"],
      awaitRefetchQueries: true,
    });
    
    onClose();
  };

  const onRestore = async () => {
    await updateStatus({
      variables: { id: calendarBlock.id, eventStatus: CalendarBlockEventStatusEnum.Scheduled },
      refetchQueries: ["CommandIndexCombinedData", "VisitAlerts", "VisitLocations"],
      awaitRefetchQueries: true,
    });
  };

  return (
    <Card elevation={3} sx={{ maxWidth: 600 }}>
      <Header calendarBlock={calendarBlock} onClose={onClose} />
      <CardContent>
        {local ? (
          <EditForm calendarBlock={local} onChange={setLocal} />
        ) : <Stack direction="column" spacing={2}>
          <Box sx={{ minWidth: 500 }}>
            <Typography variant="button">{startCase(calendarBlock.blockType)}</Typography>
            <Typography variant="body1" gutterBottom component="div">
              {timeRangeLabel(calendarBlock.startTime, calendarBlock.endTime, false, false, calendarBlock.addressTimezone ?? undefined)}{' '}
              {calendarBlock.addressTimezone && timezoneAbbreviation(calendarBlock.addressTimezone)}
            </Typography>

            {calendarBlock.title && <>
              <Typography variant="button">Title</Typography>
              <Typography variant="body1" gutterBottom>{calendarBlock.title}</Typography>
            </>}
            
            {calendarBlock.addressStreet && <>
              <Typography variant="button">Location</Typography>
              <Typography variant="body1" gutterBottom>
                {calendarBlock.addressStreet}
                {calendarBlock.addressLineTwo && `, ${calendarBlock.addressLineTwo}`}
                <br />
                {calendarBlock.addressCity}{calendarBlock.addressCity && calendarBlock.addressState && ', '}
                {calendarBlock.addressState} {calendarBlock.addressZip}
              </Typography>
            </>}

            {calendarBlock.filters?.visitTypeIds && calendarBlock.filters?.visitTypeIds?.length > 0 ? (
              <>
                <Typography variant="button">Visit Types</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {calendarBlock.filters.visitTypeIds.map((visitTypeId) => {
                    const visitType = visitTypeByKey[visitTypeId];
                    return visitType ? (
                      <Chip
                        key={visitTypeId}
                        label={visitType.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : null;
                  })}
                </Box>
              </>
            ) : null}
            
          </Box>
        </Stack>}
      </CardContent>
      <PermissionOrHide permission={Permission.manageCalendarBlocks}>
        <CardActions sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
          {calendarBlock.eventStatus === CalendarBlockEventStatusEnum["Scheduled"] && <>
            <Button color="error" onClick={(e) => setAnchorEl(e.currentTarget)} loading={loading || statusLoading}>
              Cancel Block
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              slotProps={{ list: { 'aria-labelledby': 'basic-button' }}}
            >
              <MenuItem onClick={onCancel} disabled={loading || statusLoading}>
                <ListItemText>Confirm Cancel</ListItemText>
              </MenuItem>
            </Menu>
          </>}
          {calendarBlock.eventStatus === CalendarBlockEventStatusEnum["Canceled"] && <>
            <Button color="primary" onClick={onRestore} loading={statusLoading} disabled={loading}>
              Restore Block
            </Button>
          </>}
          <Button color="primary" onClick={onEditSave} loading={loading} disabled={statusLoading || (!!local && !isCalendarBlockValid(local))}>
           {local ? "Save" : "Edit"}
          </Button>
        </CardActions>
      </PermissionOrHide>
    </Card>
  );
}

const Header = ({ calendarBlock, onClose }: { calendarBlock: Props["calendarBlock"], onClose: () => void }) => (<CardHeader
  sx={{
    borderBottom: 1,
    borderColor: "divider",
    flex: 1,
    flexDirection: "row",
  }}
  action={<IconButton aria-label="close" onClick={onClose}>
    <Close />
  </IconButton>}
  title={<Typography variant="h6">
    {calendarBlock.provider?.firstName} {calendarBlock.provider?.lastName}
    <Chip label={capitalize(calendarBlock.eventStatus)} color={calendarBlock.eventStatus === CalendarBlockEventStatusEnum.Scheduled ? "info" : "warning"} size="small" sx={{ ml: 1.5, verticalAlign: "text-top" }} />
    {calendarBlock.availabilityId && <>
      {calendarBlock.manualEdits ? (
        <Tooltip title={`This was generated as part of a recurring ${defaultCalendarBlockTitles[calendarBlock.blockType]?.toLowerCase()}, but it has been manualled edited. Changes to the recurring ${defaultCalendarBlockTitles[calendarBlock.blockType]?.toLowerCase()} will not update it.`}>
          <Chip label="Manual Edits" color="warning" size="small" sx={{ cursor: "help", ml: 1.5, verticalAlign: "text-top" }}/>
        </Tooltip>
      ) :(
        <Tooltip title={`This ${defaultCalendarBlockTitles[calendarBlock.blockType]?.toLowerCase()} is recurring. From this dialog, you can edit a specific instance; click here to edit the recurrence.`}>
          <Link to="/availability" state={{ date: startOfDay(calendarBlock.startTime) }}>
            <Chip label="Recurring" color="warning" size="small" sx={{ ml: 1.5, verticalAlign: "text-top" }}/>
          </Link>
        </Tooltip>
      )}
      </>}
  </Typography>}
/>);

gql(`
  mutation UpdateCalendarBlock(
    $id: Int!
    $providerId: Int!
    $startTime: String!
    $endTime: String!
    $title: String
    $addressStreet: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $addressTimezone: String
    $filters: CalendarBlockFilterInput
    $addressPlaceId: String
  )
  @permission(requires: manageCalendarBlocks)
  {
    updateCalendarBlock(
      id: $id
      providerId: $providerId
      startTime: $startTime
      endTime: $endTime
      title: $title
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressTimezone: $addressTimezone
      filters: $filters
      addressPlaceId: $addressPlaceId
    ) {
      id
      providerId
      startTime
      endTime
      title
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressTimezone
      addressCoordinates
      addressTimezone
      startOfShiftDriveTime
      interVisitDriveTime
      endOfShiftDriveTime
      filters {
        visitTypeIds
      }
    }
  }
`);

gql(`
  mutation UpdateCalendarBlockStatus(
    $id: Int!
    $eventStatus: CalendarBlockEventStatusEnum!
  )
  @permission(requires: manageCalendarBlocks)
  {
    updateCalendarBlock(
      id: $id
      eventStatus: $eventStatus
    ) {
      id
      eventStatus
    }
  }
`);
